<template>
  <a-badge
    :count="counter.msgCount"
    :overflow-count="99"
    :number-style="{
      top: '-5px'
    }"
    class="mr10 fs18"
  >
    <BellOutlined @click="showDrawer" data-guide-id="message-bell" />
  </a-badge>
  <a-drawer
    title="消息中心"
    class="small"
    v-model:open="visible"
    width="25%"
    @after-open-change="handleAfterVisibleChange"
  >
    <a-list
      item-layout="horizontal"
      :dataSource="dataList"
      data-guide-id="message-list"
    >
      <template #renderItem="{ item }">
        <a-list-item
          @click="readMsg(item)"
          class="cursor-pointer custom-list-item"
        >
          <template #actions>
            <a-badge count="1" v-show="item.status === 0" />
          </template>
          <a-list-item-meta>
            <template #description>
              <a-tooltip :title="item.content">
                <span class="ellipsis-desc">{{ item.content }}</span>
              </a-tooltip>
            </template>
            <template #title>
              <a-tooltip :title="item.title">
                <span class="ellipsis-title">{{ item.title }}</span>
              </a-tooltip>
            </template>
            <template #avatar>
              <a-avatar
                :size="40"
                gap="4"
                :style="{ backgroundColor: getAvatarColor(item.level) }"
              >
                {{ item.groupName?.charAt(0) }}
              </a-avatar>
            </template>
          </a-list-item-meta>
        </a-list-item>
      </template>
      <template #header>
        <div style="text-align: right; padding-right: 5px">
          <a-dropdown placement="bottomRight">
            <SmallDashOutlined
              style="font-size:18px;"
              data-guide-id="message-dropdown"
            />
            <template #overlay>
              <a-menu>
                <a-menu-item @click="openMore">
                  <a>查看更多</a>
                </a-menu-item>
                <a-menu-item @click="openMore">
                  <a>消息订阅设置</a>
                </a-menu-item>
                <a-menu-item @click="readMsg(null)">
                  <a>全部标记已读</a>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </template>
    </a-list>
  </a-drawer>
</template>

<script>
import { BellOutlined, SmallDashOutlined } from '@ant-design/icons-vue'
import {
  reactive,
  toRefs,
  defineExpose,
  inject,
  nextTick,
  onMounted
} from 'vue'
import { useStore } from 'vuex'
import { query_message_list, read_message } from 'server/modules/message.js'
import { notification } from 'ant-design-vue'
import guideService from '@/utils/guide'

export default {
  name: 'Message',
  components: {
    BellOutlined,
    SmallDashOutlined
  },
  setup() {
    const store = useStore()
    const counter = inject('useCount')

    //注入navBarState
    const navBarState = inject('navBarState')

    const state = reactive({
      visible: false,
      dataList: [],
      isLoading: false,
      pagination: {
        current: 1,
        pageSize: 100
      },
      showDrawer: async () => {
        state.visible = true
        counter.msgCount = 0
        const { success, data } = await query_message_list({
          status: 0,
          channelType: 1,
          pageNum: state.pagination.current,
          pageSize: state.pagination.pageSize
        })

        state.dataList = (success && data && data.list) || []
        console.log(state.dataList)

        // 抽屉完全打开后再启动引导
        state.checkAndShowGuide('messageCenter', [
          {
            element: document.querySelector('[data-guide-id="message-list"]'),
            intro:
              '显示最近100条未读消息；头像颜色代表消息等级，绿色代表通知类消息，橙色代表警告类消息，红色代表错误类消息；点击单条消息可标记为已读',
            position: 'left'
          },
          {
            element: document.querySelector(
              '[data-guide-id="message-dropdown"]'
            ),
            intro:
              '点击更多按钮可以进行其他操作，如查看所有消息、设置消息订阅或标记所有消息为已读',
            position: 'left'
          }
        ])
      },

      // 标记单条消息已读
      readMsg: async item => {
        console.log(item)
        const param = item
          ? { noticeIds: [item.id], status: 1 }
          : { noticeIds: null, status: 1 }
        const { success } = await read_message(param)
        if (success) {
          item ? (item.status = 1) : state.dataList.forEach(i => (i.status = 1))
        }
      },

      // 打开站内消息窗口
      openMore: () => {
        state.visible = false
        navBarState.showNoticeInfoList = true
        nextTick(() => {
          navBarState.noticeActiveKey = 4
        })
      },

      // 添加消息
      appendMsg: data => {
        if (data) {
          counter.msgCount++
          if (data.remind && data.remind === 1) {
            state.showBrowserNotification(data.title, data.content, data.level)
          }
        }
      },

      // 新增浏览器通知函数
      showBrowserNotification: (title, content, level) => {
        // 检查浏览器是否支持 Notification API
        if (!('Notification' in window)) {
          // 不支持浏览器通知，使用原来的 antd 通知
          notification.info({
            message: title,
            description: content
          })
          return
        }

        // 检查通知权限状态
        if (Notification.permission === 'granted') {
          // 已授权，直接显示通知
          state.createNotification(title, content, level)
        } else if (Notification.permission !== 'denied') {
          // 没有拒绝授权，请求授权
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              state.createNotification(title, content, level)
            } else {
              // 授权被拒绝，回退到 antd 通知
              notification.info({
                message: title,
                description: content
              })
            }
          })
        } else {
          // 权限被拒绝，回退到 antd 通知
          notification.info({
            message: title,
            description: content
          })
        }
      },

      //创建通知的辅助函数
      createNotification: (title, content, level) => {
        // 根据消息级别选择不同图标
        let iconPath = '/favicon.ico'
        switch (level) {
          case 30:
            iconPath = '/error.jpg'
            break
          case 20:
            iconPath = '/warning.jpg'
            break
          case 10:
            iconPath = '/info.jpg'
            break
          default:
            iconPath = '/info.jpg'
        }
        const notification = new Notification(title, {
          body: content,
          icon: iconPath, // 可以替换为你的应用图标路径
          badge: '/favicon.ico'
        })

        // 可选：添加通知点击事件处理
        notification.onclick = function() {
          // 点击通知时聚焦到窗口
          window.focus()
          // 可以在这里添加其他操作，如跳转到消息页面
          this.close()
        }
      },

      handleAfterVisibleChange: visible => {
        if (visible) {
          // 抽屉完全打开后再启动引导
          setTimeout(() => {
            state.checkAndShowGuide()
          }, 300)
        }
      },

      // 检查并显示引导
      checkAndShowGuide: (key, steps) => {
        setTimeout(() => {
          // 注册消息中心引导步骤
          guideService.registerGuide(key, steps)

          // 检查是否显示过引导
          if (!guideService.hasShownGuide(key)) {
            guideService.startGuide(key, () => {
              guideService.markGuideAsShown(key)
            })
          }
        }, 1000)
      },

      getAvatarColor: level => {
        switch (level) {
          case 30:
            return '#ffa39e' // 红色 - 错误类消息
          case 20:
            return '#ffd591' // 橙色 - 警告类消息
          case 10:
            return '#b7eb8f' // 绿色 - 通知类消息
          default:
            return '#b7eb8f' // 默认绿色
        }
      }
    })

    defineExpose({
      appendMsg: state.appendMsg
    })

    onMounted(() => {
      nextTick(() => {
        const token = localStorage.getItem('token')
        if (!token) {
          return
        }

        state.checkAndShowGuide('messageBar', [
          {
            element: document.querySelector('[data-guide-id="message-bell"]'),
            intro:
              '这里显示未读消息数量，数字代表未读消息条数；点击铃铛图标可以打开消息中心查看详细信息',
            position: 'bottom'
          }
        ])
      })
    })

    return {
      counter,
      ...toRefs(state)
    }
  }
}
</script>

<style lang="scss">
.ellipsis-title {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ellipsis-desc {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.custom-list-item {
  .ant-list-item-action {
    margin-left: 0;
    min-width: 28px;
  }
}
</style>
